import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  STALLED_MESSAGE,
  emptyCollection,
  escapeHtml,
  popActions,
  setData,
  useMapInstance,
  wirePopActions,
} from '../map/mapbase';

export interface OrderPin {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  status: string;
  technician: string | null;
  /** Everything the clicked bubble says beyond the name. */
  reference: string | null;
  customer: string | null;
  address: string;
  eta: string;
  /** `10.1` -- what the board, the driver and the dispatcher all call it. */
  label: string | null;
}

/**
 * Where the day's work is.
 *
 * The dispatch map answers "how are the routes going"; this one answers "where
 * is this order", which is a different question and wants a different drawing.
 * Colour carries the order's own state rather than which route it landed on,
 * and the selected pin is drawn large so it can be found after a search
 * narrowed the list to one row.
 */
export function OrdersMap({
  orders,
  selectedId,
  onSelect,
  adjusting,
  onPinMove,
  fitKey,
  onUnassignOrder,
  onDeleteOrder,
}: {
  orders: OrderPin[];
  /**
   * Changes when the caller has narrowed what it is showing, which re-frames
   * the map. The viewport is otherwise framed once and then left alone -- a map
   * that re-centres itself while being read is worse than one that does not.
   */
  fitKey?: string;
  selectedId: string | null;
  onSelect: (orderId: string) => void;
  /**
   * The position being adjusted, while an order is open for editing. Held here
   * rather than read from `orders` because it moves with the drag, before
   * anything is saved.
   */
  adjusting: { lat: number; lng: number } | null;
  onPinMove: (lat: number, lng: number) => void;
  /**
   * The same two actions the dispatch map's bubble offers, for the same
   * reason: the pin is where the dispatcher already is. Unassign is offered
   * only for an order that is on a route; delete asks first.
   */
  onUnassignOrder?: (orderId: string, orderName: string) => void;
  onDeleteOrder?: (orderId: string, orderName: string) => void;
}) {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onPinMoveRef = useRef(onPinMove);
  onPinMoveRef.current = onPinMove;
  const onUnassignRef = useRef(onUnassignOrder);
  onUnassignRef.current = onUnassignOrder;
  const onDeleteRef = useRef(onDeleteOrder);
  onDeleteRef.current = onDeleteOrder;
  const pinRef = useRef<maplibregl.Marker | null>(null);
  /* The bubble on the selected order, and which order it belongs to. */
  const detailRef = useRef<maplibregl.Popup | null>(null);
  const detailForRef = useRef<string | null>(null);
  const fittedRef = useRef(false);
  const fitKeyRef = useRef(fitKey);

  const { containerRef, mapRef, ready, stalled } = useMapInstance((map) => {
    map.addSource('orders', { type: 'geojson', data: emptyCollection() as never });

    map.addLayer({
      id: 'orders',
      type: 'circle',
      source: 'orders',
      paint: {
        /*
         * Larger than they were. At 5px a pin was a dot the eye slid off and a
         * target a mouse missed; the selected one has to be findable in a
         * cluster, so it is nearly half again as big as its neighbours.
         */
        'circle-radius': ['case', ['get', 'selected'], 11, 7.5],
        'circle-color': [
          'case',
          ['==', ['get', 'status'], 'completed'], '#1c7f4d',
          ['==', ['get', 'status'], 'failed'], '#b93a2b',
          ['==', ['get', 'status'], 'cancelled'], '#93a3ad',
          /*
           * White, which on this screen means "nobody has picked this up".
           * Amber read as a warning beside the red of a failure, when an
           * unrouted order is not a fault in the day -- it is the work still
           * to be given out, and an empty pin says so at a glance.
           */
          ['==', ['get', 'status'], 'unassigned'], '#ffffff',
          '#0f6fbf',
        ],
        'circle-opacity': 0.92,
        'circle-stroke-width': ['case', ['get', 'selected'], 3.5, 1.6],
        /*
         * A white pin needs a dark edge or it vanishes into the basemap.
         * Everything else keeps the white ring that lifts it off the streets.
         */
        'circle-stroke-color': [
          'case',
          ['==', ['get', 'status'], 'unassigned'], '#4d5f6b',
          '#ffffff',
        ],
      },
    });

    /*
     * A click only says which order it is. Opening the bubble is the
     * selection's job, below -- so an order picked from the list on the left
     * gets exactly the same treatment as one picked off the map, which is the
     * whole point: a dispatcher clicking a row wants to see where it is.
     */
    map.on('click', 'orders', (event) => {
      const id = event.features?.[0]?.properties?.id as string | undefined;
      if (id) onSelectRef.current(id);
    });

    const popup = new maplibregl.Popup({ focusAfterOpen: false, closeButton: false, closeOnClick: false, offset: 10 });

    map.on('mouseenter', 'orders', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'orders', () => {
      map.getCanvas().style.cursor = '';
    });

    map.on('mousemove', 'orders', (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      const p = feature.properties as Record<string, string>;
      /* The open bubble already says all of this, in the same place. */
      if (p.id === detailForRef.current) {
        popup.remove();
        return;
      }
      popup
        .setLngLat((feature.geometry as GeoJSON.Point).coordinates as [number, number])
        .setHTML(
          `<b>${escapeHtml(p.name ?? '')}</b><br>` +
            `<span style="color:#4d5f6b">${escapeHtml(p.technician || 'unassigned')}</span>`,
        )
        .addTo(map);
    });
    map.on('mouseleave', 'orders', () => popup.remove());
  });

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const points = {
      type: 'FeatureCollection' as const,
      features: orders
        .filter((o) => o.lat !== null && o.lng !== null)
        .map((o) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [o.lng as number, o.lat as number],
          },
          properties: {
            id: o.id,
            name: o.name,
            status: o.status,
            technician: o.technician ?? '',
            reference: o.reference ?? '',
            customer: o.customer ?? '',
            address: o.address,
            eta: o.eta,
            label: o.label ?? '',
            selected: o.id === selectedId,
          },
        })),
    };

    setData(map, 'orders', points);

    /*
     * Frame the day once, then leave the viewport alone -- unless the caller
     * says what is being shown has changed, which is a new day's worth of
     * framing rather than a redraw of the same one.
     */
    if (fitKeyRef.current !== fitKey) {
      fitKeyRef.current = fitKey;
      fittedRef.current = false;
    }
    if (!fittedRef.current && points.features.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      for (const f of points.features) {
        bounds.extend(f.geometry.coordinates as [number, number]);
      }
      map.fitBounds(bounds, { padding: 56, duration: 0 });
      fittedRef.current = true;
    }
  }, [orders, selectedId, ready, mapRef, fitKey]);

  /*
   * The selected order, brought to the middle and described.
   *
   * It used to pan only when the pin happened to be off screen and never say
   * anything, so clicking a row in the list moved nothing and a dispatcher had
   * to find the pin themselves. Now it centres the map and opens the same
   * bubble a click on the pin does -- one behaviour, whichever way the order
   * was chosen.
   *
   * `easeTo`, not `fitBounds`: the zoom the dispatcher set is part of what they
   * are reading, and picking one order is no reason to change it.
   */
  /*
   * Keyed on the selected order's own details, not on the whole list. The list
   * changes with every keystroke in the search box; tied to it, the bubble was
   * torn down and reopened -- and the map re-centred -- on each letter typed.
   */
  const selectedOrder = selectedId ? (orders.find((o) => o.id === selectedId) ?? null) : null;
  const selectedKey = selectedOrder
    ? JSON.stringify([
        selectedOrder.id,
        selectedOrder.lat,
        selectedOrder.lng,
        selectedOrder.name,
        selectedOrder.label,
        selectedOrder.technician,
        selectedOrder.eta,
        selectedOrder.customer,
        selectedOrder.address,
        selectedOrder.reference,
        selectedOrder.status,
      ])
    : null;
  const selectedOrderRef = useRef(selectedOrder);
  selectedOrderRef.current = selectedOrder;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    detailRef.current?.remove();
    detailRef.current = null;
    detailForRef.current = null;
    if (!selectedKey) return;

    const order = selectedOrderRef.current;
    if (!order || order.lat === null || order.lng === null) return;

    map.easeTo({ center: [order.lng, order.lat], duration: 420 });

    const detail = new maplibregl.Popup({ focusAfterOpen: false, closeButton: true, closeOnClick: false, offset: 16 })
      .setLngLat([order.lng, order.lat])
      .setHTML(
        `<b>${escapeHtml(order.name)}</b>` +
          (order.label ? `<div class="map-pop-line">Stop ${escapeHtml(order.label)}</div>` : '') +
          `<div class="map-pop-line">${escapeHtml(order.technician || 'unassigned')}` +
          `${order.eta ? ` · ${escapeHtml(order.eta)}` : ''}</div>` +
          (order.customer ? `<div class="map-pop-line">${escapeHtml(order.customer)}</div>` : '') +
          (order.address ? `<div class="map-pop-line">${escapeHtml(order.address)}</div>` : '') +
          (order.reference ? `<div class="map-pop-line">${escapeHtml(order.reference)}</div>` : '') +
          popActions({
            /* Nothing to unassign from when it is not on a route. */
            unassign: Boolean(onUnassignRef.current) && order.status !== 'unassigned',
            remove: Boolean(onDeleteRef.current),
          }),
      )
      .addTo(map);

    wirePopActions(detail, {
      unassign: () => onUnassignRef.current?.(order.id, order.name),
      remove: () => onDeleteRef.current?.(order.id, order.name),
    });

    detailRef.current = detail;
    detailForRef.current = order.id;
    return () => {
      detail.remove();
    };
  }, [selectedKey, ready, mapRef]);

  /*
   * A draggable pin, shown only while an order is being edited.
   *
   * A real marker rather than a point in the layer: the layer is data, and this
   * is a control. Dragging it reports upward on every frame, so the coordinates
   * in the form and the pin on the map are never briefly telling different
   * stories.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!adjusting) {
      pinRef.current?.remove();
      pinRef.current = null;
      return;
    }

    if (!pinRef.current) {
      const element = document.createElement('div');
      element.className = 'map-pin';
      element.title = 'Drag to correct where this order is';
      const marker = new maplibregl.Marker({ element, draggable: true, anchor: 'bottom' })
        .setLngLat([adjusting.lng, adjusting.lat])
        .addTo(map);
      marker.on('drag', () => {
        const { lat, lng } = marker.getLngLat();
        onPinMoveRef.current(lat, lng);
      });
      pinRef.current = marker;
      /* Bring it into view: a pin you cannot see is one you cannot drag. */
      if (!map.getBounds().contains([adjusting.lng, adjusting.lat])) {
        map.easeTo({ center: [adjusting.lng, adjusting.lat], duration: 400 });
      }
    } else {
      /* Only follow the props when the drag is not the thing moving it. */
      const current = pinRef.current.getLngLat();
      if (
        Math.abs(current.lat - adjusting.lat) > 1e-9 ||
        Math.abs(current.lng - adjusting.lng) > 1e-9
      ) {
        pinRef.current.setLngLat([adjusting.lng, adjusting.lat]);
      }
    }
  }, [adjusting, mapRef]);

  useEffect(() => {
    const marker = pinRef.current;
    return () => {
      marker?.remove();
    };
  }, []);

  return (
    <div className="map-wrap">
      <div className="map" ref={containerRef} />
      {stalled ? (
        <div className="map-stalled" role="status">
          <b>The basemap did not load</b>
          <p>Orders and their positions are unaffected. {STALLED_MESSAGE}</p>
        </div>
      ) : null}
      <div className="map-legend">
        <span><i style={{ background: 'var(--accent)' }} />Planned</span>
        <span><i style={{ background: 'var(--ok)' }} />Completed</span>
        <span><i style={{ background: 'var(--late)' }} />Failed</span>
        {/* The swatch has to carry the same dark edge the pin does, or a
            white dot on a white legend is nothing at all. */}
        <span>
          <i style={{ background: '#ffffff', boxShadow: 'inset 0 0 0 1.5px #4d5f6b' }} />
          Unassigned
        </span>
      </div>
    </div>
  );
}
